import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";

import {
  confirmTransactionPinReset,
  requestTransactionPinReset,
} from "../../../services/UserService";
import { updateAuthUser } from "../../../store/actions/Auth/AuthActions";
import { showMutationError } from "../../../utils/showMutationError";
import PinInput from "../PinInput";

const emptyForm = { password: "", otp: "", newPin: "", newPinConfirmation: "" };

const ForgotPinModal = ({ show, onHide }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(emptyForm);
  const [otpSent, setOtpSent] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const close = () => {
    setFormData(emptyForm);
    setOtpSent(false);
    setPasswordError(null);
    onHide();
  };

  const handleInput = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (id === "password") setPasswordError(null);
  };

  const requestMutation = useMutation({
    mutationFn: requestTransactionPinReset,
    onSuccess: (response) => {
      toast.success(response.data?.message || "We have emailed you a one-time code. Enter it below to reset your PIN.");
      setOtpSent(true);
    },
    onError: (err) => {
      const fieldError = err?.response?.data?.errors?.password?.[0];
      if (fieldError) {
        setPasswordError(fieldError);
        return;
      }
      showMutationError(err, "Could not send the one-time code. Please try again.");
    },
  });

  const confirmMutation = useMutation({
    mutationFn: confirmTransactionPinReset,
    onSuccess: (response) => {
      toast.success(response.data?.message || "Transaction PIN reset successfully.");
      dispatch(updateAuthUser({ has_transaction_pin: true, transaction_pin_expiry_date: null }));
      close();
    },
    onError: (err) => showMutationError(err, "Could not reset your transaction PIN. Please try again."),
  });

  const handleRequest = () => {
    setPasswordError(null);
    requestMutation.mutate({
      partnerId: authUser?.partner_id,
      userId: authUser?.id,
      password: formData.password,
    });
  };

  const handleConfirm = () => {
    confirmMutation.mutate({
      partnerId: authUser?.partner_id,
      userId: authUser?.id,
      ...formData,
    });
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Forgot Transaction PIN</Modal.Title>
      </Modal.Header>
      {!otpSent ? (
        <Form onSubmit={(e) => { e.preventDefault(); handleRequest(); }}>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Confirm your account password. We will then email a six-digit one-time code to <strong>{authUser?.email}</strong> that you enter on the next screen along with a new PIN.
            </p>

            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Account Password</Form.Label>
              <Form.Control
                type="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInput}
                isInvalid={!!passwordError}
                placeholder="Your Milipot account password"
              />
              <Form.Control.Feedback type="invalid">
                {passwordError}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={close} disabled={requestMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!formData.password || requestMutation.isPending}
            >
              {requestMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
              Email me a one-time code
            </Button>
          </Modal.Footer>
        </Form>
      ) : (
        <Form onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Check <strong>{authUser?.email}</strong> for the one-time code. It expires shortly after being sent.
            </p>

            <div className="mb-3">
              <label className="form-label" htmlFor="otp">
                One-Time Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="123456"
                id="otp"
                value={formData.otp}
                onChange={handleInput}
                autoComplete="one-time-code"
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="newPin">
                New Transaction PIN
              </label>
              <PinInput
                placeholder="******"
                id="newPin"
                value={formData.newPin}
                onChange={handleInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="newPinConfirmation">
                Confirm New Transaction PIN
              </label>
              <PinInput
                placeholder="******"
                id="newPinConfirmation"
                value={formData.newPinConfirmation}
                onChange={handleInput}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="link"
              className="p-0"
              onClick={handleRequest}
              disabled={requestMutation.isPending}
            >
              Resend code
            </Button>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={close} disabled={confirmMutation.isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  !formData.otp ||
                  !formData.newPin ||
                  !formData.newPinConfirmation ||
                  confirmMutation.isPending
                }
              >
                {confirmMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
                Reset PIN
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

export default ForgotPinModal;
